import { getCookie } from "@insite/client-framework/Common/Cookies";
import { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setFlyoutDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetFlyoutDrawerIsOpen";
import setInitialValues from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetInitialValues";
import setNavDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetNavDrawerIsOpen";
import setSignInOverlayIsOpen from "@insite/client-framework/Store/Components/SignInOverlay/Handlers/SetSignInOverlayIsOpen";
import {
    getCurrencies,
    getFulfillmentLabel,
    getIsPunchOutSession,
    getLanguages,
    getSettingsCollection,
} from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import setCurrency from "@insite/client-framework/Store/Context/Handlers/SetCurrency";
import setLanguage from "@insite/client-framework/Store/Context/Handlers/SetLanguage";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import removeCart from "@insite/client-framework/Store/Data/Carts/Handlers/RemoveCart";
import { getHeader, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getWidgetsByPageId } from "@insite/client-framework/Store/Data/Widgets/WidgetSelectors";
import { getPageLinkByPageType, LinkModel, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import { ShowInventoryToggleStyles } from "@insite/content-library/Components/ShowInventoryToggle";
import { ShowPricingToggleStyles } from "@insite/content-library/Components/ShowPricingToggle";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import Drawer, { DrawerPresentationProps } from "@insite/mobius/Drawer";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconProps } from "@insite/mobius/Icon";
import { MappedLink } from "@insite/mobius/Menu";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import omitSingle from "@insite/mobius/utilities/omitSingle";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import signOut from "@ucs/Handlers/UcsSignOut";
import { Roles } from "@ucs/Helpers/Enums";
import User from "@ucs/Icons/Account";
import AccountRounded from "@ucs/Icons/AccountRounded";
import ChevronLeft from "@ucs/Icons/ChevronLeftRound";
import ChevronRight from "@ucs/Icons/ChevronSmallRight";
import Close from "@ucs/Icons/Close";
import Menu from "@ucs/Icons/Hamburger";
import PanelMenu, { PanelMenuPresentationProps } from "@ucs/MobiusOverrides/PanelMenu/PanelMenu";
import PanelRow, { PanelRowPresentationProps } from "@ucs/MobiusOverrides/PanelMenu/PanelRow";
import setIsLocalSelectedPLP from "@ucs/Store/Custom/SetIsLocalSelectedPLP";
import UCSColors from "@ucs/Utils/UCSColors";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const headerLinkListLinkFields =
        (getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
            return widget.type === "Header/HeaderLinkList";
        })?.fields.links as LinkModel[]) ?? [];
    const displayPricingAndInventory = state.context.session?.displayPricingAndInventory || false;
    const currentMode = getCookie(NavigationModeCookieName) || "Storefront";

    return {
        currencies: getCurrencies(state),
        currentCurrencyId: state.context.session.currency?.id,
        currentCurrencySymbol: state.context.session.currency?.currencySymbol,
        languages: getLanguages(state),
        currentLanguage: state.context.session.language,
        currentLocation: getLocation(state),
        isSigningIn: state.context.isSigningIn,
        userName: state.context.session?.userName,
        isGuest: state.context.session?.isGuest,
        myAccountPageLink: getPageLinkByPageType(state, "MyAccountPage"),
        signInUrl: getPageLinkByPageType(state, "SignInPage"),
        vmiDashboardPageUrl: getPageLinkByPageType(state, "VmiDashboardPage")?.url,
        homePageUrl: getPageLinkByPageType(state, "HomePage")?.url,
        headerLinkListLinkFields,
        showCustomerMenuItem: getSettingsCollection(state).accountSettings.enableWarehousePickup,
        fulfillmentLabel: getFulfillmentLabel(state),
        navDrawerIsOpen: state.components.addressDrawer.navDrawerIsOpen,
        flyoutDrawerIsOpen: state.components.addressDrawer.flyoutDrawerIsOpen,
        isPunchOutSession: getIsPunchOutSession(state),
        isAccounting: state.context.session!.userRoles
            ? state.context.session!.userRoles.includes(Roles.Accounting)
            : false,
        cart: getCurrentCartState(state).value,
        visibilityState: findVisibilityState(state.data.pages.widgetsById),        
        websiteName: state.context.website.name,
        displayPricingAndInventoryToggles: displayPricingAndInventory && currentMode === "Storefront",
    };
};

const mapDispatchToProps = {
    setCurrency,
    setLanguage,
    signOut,
    cancelPunchOut,
    setInitialValues,
    setNavDrawerIsOpen,
    setIsLocalSelectedPLP,
    setFlyoutDrawerIsOpen,
    removeCart,
    setSignInOverlayIsOpen,
};

interface OwnProps {
    links: MappedLink[];
    showQuickOrder: boolean;
    quickOrderLink: PageLinkModel | undefined;
    displayVmiNavigation?: boolean;
    vmiPageLinks?: (PageLinkModel | undefined)[];
    displayModeSwitch?: boolean;
    currentMode?: string;
    isCompactHeaderFlyout?: boolean;
    showFlyoutPricingToggle?: boolean;
    showFlyoutInventoryToggle?: boolean;
    rootElementSelector?: string;
    allowOpenParentLinks?: boolean;
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory &
    ThemeProps<BaseTheme>;

export interface NavigationDrawerStyles {
    menuTriggerButton?: ButtonPresentationProps;
    drawer?: DrawerPresentationProps;
    drawerSectionWrapper?: InjectableCss;
    currencySelectorWrapper?: InjectableCss;
    languageSelectorWrapper?: InjectableCss;
    panelMenu?: PanelMenuPresentationProps;
    panelSectionWrapper?: InjectableCss;
    menuRowIcon?: IconProps;
    userRowTypography?: TypographyPresentationProps;
    punchOutUserRowText?: TypographyPresentationProps;
    mainNavigationRow?: PanelRowPresentationProps;
    mainNavigationRowAccount?: PanelRowPresentationProps;
    mainNavigationRowIcon?: IconProps;
    mainNavigationRowIconLoggedOut?: IconProps;
    punchOutNavigationRowIcon?: IconProps;
    mainNavigationRowTypography?: TypographyPresentationProps;
    logoLinks?: PanelRowPresentationProps & { typographyProps: TypographyPresentationProps };
    currencySymbol?: TypographyPresentationProps;
    changeCustomerRow?: PanelRowPresentationProps;
    changeCustomerRowContainer?: GridContainerProps;
    fulfillmentMethodGridItem?: GridItemProps;
    addressesGridItem?: GridItemProps;
    pickUpAddressGridItem?: GridItemProps;
    applyButtonGridItem?: GridItemProps;
    showInventoryToggle?: ShowInventoryToggleStyles;
    showPricingToggle?: ShowPricingToggleStyles;
    signOutRow?: PanelRowPresentationProps;
    signOutRowText?: TypographyPresentationProps;
}

const StyledSection = getStyledWrapper("section");
const StyledSpan = getStyledWrapper("span");

const mainNavigationRowIconCSS = css`
    margin-top: -2px;
    margin-right: 20px;
    width: 24px;
    display: inline-block;
    text-align: center;
    vertical-align: middle;
`;

const mainNavigationRowIcon = {
    src: AccountRounded,
    color: UCSColors.gray10,
    size: 22,
    css: mainNavigationRowIconCSS,
};

const mainNavigationRowIconLoggedOut = {
    ...mainNavigationRowIcon,
    src: User,
    css: css`
        ${mainNavigationRowIconCSS}
        opacity: .8;
    `,
};

export const navigationDrawerStyles: NavigationDrawerStyles = {
    menuTriggerButton: {
        shape: "pill",
        // color: "secondary",
        size: 50,
    },
    drawer: {
        size: 9999,
        cssOverrides: {
            drawerContent: css`
                margin-top: -2px;
                overflow-x: hidden;
                width: 100% !important;
                font-weight: normal;
                background-color: ${UCSColors.gray09};
                height: 100%;
            `,
        },
    },
    drawerSectionWrapper: {
        css: css`
            &:first-child {
                margin-top: 0;
                /* padding-bottom: 10px; */
                /* background: ${getColor("common.backgroundContrast")}; */
            }
            &:last-child {
                margin-bottom: 5px;
                border-bottom: none !important;
            }
            /* margin-top: 20px; */
        `,
    },
    panelMenu: {
        cssOverrides: {
            wrapper: css`
                #panelChangeCustomer {
                    &:last-child {
                        border-bottom: none !important;
                    }
                }

                > button {
                    width: 100%;
                    border-top: none !important;
                }
            `,
        },
    },
    panelSectionWrapper: {
        css: css`
            height: 22px;
            display: flex;
            align-items: center;
            $:last-child {
                border-bottom: none !important;
            }
        `,
    },
    menuRowIcon: {
        size: 22,
        css: css`
            margin-top: -2px;
            margin-right: 10px;
            width: 24px;
            display: inline-block;
            text-align: center;
            vertical-align: middle;
        `,
    },
    userRowTypography: {
        // transform: "uppercase",
        color: "common.background",
        size: 18,
    },
    punchOutUserRowText: {
        // transform: "uppercase",
        color: "common.background",
        size: 18,
    },
    mainNavigationRow: {
        color: "white",
        css: css`
            padding: 15px;
            padding-left: 70px;
            background-color: ${UCSColors.gray09} !important;
            font-size: 18px;
            border-bottom: 1px solid ${UCSColors.gray07} !important;
            &:first-child {
                border-top: 1px solid ${UCSColors.gray07};
            }
            &:last-child {
                border-top: none !important;
                border-bottom: none !important;
            }
        `,
    },
    signOutRow: {
        color: "white",
        css: css`
            padding: 15px;
            padding-left: 70px;
            background-color: ${UCSColors.gray09} !important;
            border-top: 1px solid ${UCSColors.gray07};
            font-size: 18px;
            width: 100%;
            color: ${UCSColors.gray03};
            // border-top: none !important;
        `,
    },
    mainNavigationRowAccount: {
        color: "white",
        css: css`
            background-color: ${getColor("primary")};
            color: white;
            padding: 25px;
            padding-right: 15px;            
        `,
    },
    mainNavigationRowIcon,
    mainNavigationRowIconLoggedOut,
    punchOutNavigationRowIcon: {
        src: User,
        color: "common.background",
        size: 22,
        css: css`
            margin-top: -2px;
            margin-right: 10px;
            width: 24px;
            display: inline-block;
            text-align: center;
            vertical-align: middle;
            opacity: 0.8;
        `,
    },
    mainNavigationRowTypography: {
        // transform: "uppercase",
        color: UCSColors.gray03,
        size: 18,
        // weight: "bold",
    },
    logoLinks: {
        color: "common.accent",
        typographyProps: {
            ellipsis: true,
            // transform: "uppercase",
        },
        css: css`
            margin: 2px;
        `,
    },
    currencySymbol: {
        size: 22,
        css: css`
            margin-top: -4px;
            margin-right: 10px;
            width: 24px;
            display: inline-block;
            text-align: center;
            vertical-align: middle;
        `,
    },
    changeCustomerRow: {
        css: css`
            &:focus {
                outline: none;
            }
        `,
    },
    changeCustomerRowContainer: { gap: 15 },
    fulfillmentMethodGridItem: { width: 12 },
    addressesGridItem: { width: 12 },
    pickUpAddressGridItem: { width: 12 },
    applyButtonGridItem: { width: 12 },
    showInventoryToggle: {
        toggleClickable: {
            css: css`
                text-transform: uppercase;
                padding: 10px 10px 10px 20px;
            `,
        },
    },
    showPricingToggle: {
        toggleClickable: {
            css: css`
                text-transform: uppercase;
                padding: 10px 10px 10px 20px;
            `,
        },
    },
    signOutRowText: {
        // transform: "uppercase",
    },
};

const styles = navigationDrawerStyles;

const NavigationDrawer: FC<Props> = ({
    links,
    showQuickOrder,
    quickOrderLink,
    isCompactHeaderFlyout,
    rootElementSelector,
    currentLocation,
    userName,
    isGuest,
    myAccountPageLink,
    signInUrl,
    headerLinkListLinkFields,
    navDrawerIsOpen,
    flyoutDrawerIsOpen,
    isPunchOutSession,
    history,
    theme,
    signOut,
    cancelPunchOut,
    setInitialValues,
    setNavDrawerIsOpen,
    setFlyoutDrawerIsOpen,
    setSignInOverlayIsOpen,
    visibilityState,
    isAccounting,
    websiteName,
}: Props) => {
    const showIcon = visibilityState === "both" || visibilityState === "icon";
    const showLabel = visibilityState === "both" || visibilityState === "label";
    const headerLinkListLinks = useGetLinks(headerLinkListLinkFields, o => o.fields.destination);
    const openDrawer = () => {
        isCompactHeaderFlyout ? setFlyoutDrawerIsOpen({ isOpen: true }) : setNavDrawerIsOpen({ navDrawerIsOpen: true });
        setInitialValues({});
    };

    const closeDrawer = () => {
        if (isCompactHeaderFlyout) {
            setFlyoutDrawerIsOpen({ isOpen: false });
            setTimeout(() => setFlyoutDrawerIsOpen({ isOpen: undefined }), 300);
            return;
        }
        setNavDrawerIsOpen({ navDrawerIsOpen: false });
        setTimeout(() => setNavDrawerIsOpen({ navDrawerIsOpen: undefined }), 300);
    };

    const closeDrawerAndSetCookie = (url?: string) => {
        setNavDrawerIsOpen({ navDrawerIsOpen: false });
        setTimeout(() => setNavDrawerIsOpen({ navDrawerIsOpen: undefined }), 300);
        if (url && (url.includes("catalogue") || url.includes("Catalog"))) {
            setIsLocalSelectedPLP({ isLocalSelectedPLP: true });
        } else {
            setIsLocalSelectedPLP({ isLocalSelectedPLP: false });
        }
    };

    const signInPanelClickHandler = (event: React.MouseEvent) => {
        if (theme.signIn?.useOverlay) {
            event.preventDefault();
            setSignInOverlayIsOpen({ isOpen: true });
        }

        closeDrawer();
    };

    const signInPanelKeyPressHandler = (event: React.KeyboardEvent) => {
        if (event.key === " " && history && signInUrl?.url) {
            event.preventDefault();

            if (theme.signIn?.useOverlay) {
                setSignInOverlayIsOpen({ isOpen: true });
            } else {
                history.push(signInUrl.url);
            }

            closeDrawer();
        }
    };

    const onSignOutHandler = () => {
        setNavDrawerIsOpen({ navDrawerIsOpen: false });
        isPunchOutSession ? cancelPunchOut() : signOut();
    };


    const currentPageUrl = currentLocation.pathname;
    const isOpen = isCompactHeaderFlyout ? flyoutDrawerIsOpen : navDrawerIsOpen;

    const accountMenuItems: any[] = Object.assign([], myAccountPageLink?.children ?? []);
    accountMenuItems.push({
        title: "Log Out",
        excludeFromNavigation: false,
        clickableProps: {
            onClick: onSignOutHandler,
        },
    });
  
    const updatedAccountMenuItems=accountMenuItems?.filter(item => 
    {
        const childrenDoesNotExists= !item.children ||   item.children?.length===0;
        const childrenAfterExclusion=item.children?.filter((child:any) => !child.excludeFromNavigation);
        return !item.excludeFromNavigation && !(item.title === "Quotes" && isAccounting) &&  (childrenDoesNotExists || 
         (!childrenDoesNotExists && childrenAfterExclusion && childrenAfterExclusion?.length > 0));
    });    
    
    React.useEffect(() => {
        const checkIfMobile = () => {
            if (window.innerWidth >= 1200) {
                closeDrawer();
            }
        };

        window.addEventListener("resize", checkIfMobile);

        return () => {
            window.removeEventListener("resize", checkIfMobile);
        };
    }, []);

    return (
        <>
            <Button
                className="menu-trigger-button"
                onClick={isOpen ? closeDrawer : openDrawer}
                {...styles.menuTriggerButton}
                data-test-selector="expandMobileMenu"
            >
                <ButtonIcon className="menu-trigger-button-icon" src={isOpen ? Close : Menu} />
                <VisuallyHidden>{translate("menu")}</VisuallyHidden>
            </Button>
            <Drawer
                className="drawer"
                draggable
                position="left"
                {...styles.drawer}
                isOpen={isOpen}
                handleClose={closeDrawer}
                contentLabel="menu drawer"
                data-test-selector="drawerSection"
                rootElementSelector={rootElementSelector}
                cssOverrides={{
                    scrim: css`
                        top: 117px;
                    `,
                }}
            >
                <StyledSection className="drawer-section-wrapper" {...styles.drawerSectionWrapper}>
                    {userName && !isGuest && !isPunchOutSession? (
                        <PanelMenu
                            className="panel-menu"
                            currentUrl={currentPageUrl}
                            panelTrigger={
                                <PanelRow
                                    className="main-navigation-row"
                                    hasChildren
                                    style={!websiteName?.toLowerCase().includes("cherry")? {} : {"display": "none"}}
                                    {...styles.mainNavigationRowAccount}
                                    moreIconProps={{ src: ChevronRight, color: "white" }}
                                >
                                    <StyledSpan className="panel-section-wrapper" {...styles.panelSectionWrapper}>
                                        <Icon className="main-navigation-row-icon" {...styles.mainNavigationRowIcon} />
                                        <Typography className="user-row-text" {...styles.userRowTypography}>
                                            {translate("My Account")}
                                        </Typography>
                                    </StyledSpan>
                                </PanelRow>
                            }
                            menuItems={updatedAccountMenuItems}
                            maxDepth={2}
                            closeOverlay={closeDrawer}
                            layer={0}
                            {...styles.panelMenu}
                            headerColor={UCSColors.gray09}
                            bodyColor={UCSColors.gray09}
                            backIconProps={{ src: ChevronLeft }}
                            panelRowProps={{
                                color: UCSColors.gray09,
                                css: css`
                                    padding: 15px;
                                    padding-left: 75px;
                                    font-size: 18px;
                                    font-weight: normal;
                                    background: ${UCSColors.gray09} !important;
                                    &:first-child {
                                        border-top: 1px solid ${UCSColors.gray07};
                                    }
                                    border-bottom: 1px solid ${UCSColors.gray07}; // Children borders
                                    &:last-child {
                                        border-bottom: none !important;
                                        border-top: none !important;
                                    }
                                `,
                            }}
                        />
                    ) : isPunchOutSession ? (
                        <PanelRow
                            className="main-navigation-row"
                            style={!websiteName?.toLowerCase().includes("cherry")? {} : {"display": "none"}}
                            {...styles.mainNavigationRowAccount}
                            moreIconProps={{ src: ChevronRight, color: "white" }}
                        >
                            <StyledSpan className="panel-section-wrapper" {...styles.panelSectionWrapper}>
                                <Icon className="punch-out-navigation-row-icon" {...styles.punchOutNavigationRowIcon} />
                                <Typography className="punch-out-user-row-text" {...styles.punchOutUserRowText}>
                                    {userName}
                                </Typography>
                            </StyledSpan>
                        </PanelRow>
                    ) : (
                        <PanelRow
                            className="main-navigation-row"
                            style={!websiteName?.toLowerCase().includes("cherry")? {} : {"display": "none"}}
                            {...styles.mainNavigationRowAccount}
                            isCurrent={currentPageUrl === signInUrl?.url}
                            onClick={signInPanelClickHandler}
                            onKeyPress={signInPanelKeyPressHandler}
                            href={signInUrl?.url}
                            forceMoreIcon={false}
                            moreIconProps={{ src: ChevronRight, color: "white" }}
                        >
                            <StyledSpan className="panel-section-wrapper" {...styles.panelSectionWrapper}>
                                {showIcon && (
                                    <Icon
                                        className="main-navigation-row-icon"
                                        {...styles.mainNavigationRowIconLoggedOut}
                                    />
                                )}
                                {showLabel && (
                                    <Typography className="user-row-text" {...styles.userRowTypography}>
                                        {translate("Sign In / Sign Up")}
                                    </Typography>
                                )}
                            </StyledSpan>
                        </PanelRow>
                    )}

                    {/* covers MainNavigation functionality */}
                    {links.map((link, index) => {
                        if (link.children && link.children.length > 0) {
                            return (
                                <PanelMenu
                                    className="panel-menu"
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={index}
                                    id="panelChangeCustomer"
                                    headerColor={UCSColors.gray09}
                                    bodyColor={UCSColors.gray09}
                                    backIconProps={{ src: ChevronLeft }}
                                    panelRowProps={{
                                        color: UCSColors.gray09,
                                        css: css`
                                            padding: 15px;
                                            padding-left: 75px;
                                            font-size: 18px;
                                            font-weight: normal;
                                            background-color: ${UCSColors.gray09} !important;
                                            color: ${UCSColors.gray01} !important;
                                            border-bottom: 1px solid ${UCSColors.gray07}; // Children borders
                                            &:first-child {
                                                border-top: 1px solid ${UCSColors.gray07};
                                            }
                                            &:last-child {
                                                border-bottom: none !important;
                                                border-top: none !important;
                                            }
                                        `,
                                    }}
                                    currentUrl={currentPageUrl}
                                    closeOverlay={() => closeDrawerAndSetCookie(link.url)}
                                    panelTrigger={
                                        <PanelRow
                                            className="main-navigation-row"
                                            hasChildren
                                            {...styles.mainNavigationRow}
                                        >
                                            <Typography
                                                className="main-navigation-row-text"
                                                {...styles.mainNavigationRowTypography}
                                            >
                                                {link.title}
                                            </Typography>
                                        </PanelRow>
                                    }
                                    menuItems={link.children}
                                    parentItem={link}
                                    maxDepth={link.maxDepth || 3}
                                    layer={0}
                                    {...styles.panelMenu}
                                />
                            );
                        }
                        return (
                            <PanelRow
                                // eslint-disable-next-line react/no-array-index-key
                                key={index}
                                href={link.url}
                                onClick={() => closeDrawerAndSetCookie(link.url)}
                                className="main-navigation-row"
                                target={link.openInNewWindow ? "_blank" : ""}
                                {...styles.mainNavigationRow}
                            >
                                <Typography
                                    className="main-navigation-row-text"
                                    {...styles.mainNavigationRowTypography}
                                >
                                    {link.title}
                                </Typography>
                            </PanelRow>
                        );
                    })}
                    {showQuickOrder && quickOrderLink && (
                        <PanelRow
                            className="main-navigation-row"
                            isCurrent={currentPageUrl === quickOrderLink.url}
                            onClick={closeDrawer}
                            href={quickOrderLink.url}
                            {...styles.mainNavigationRow}
                        >
                            <Typography className="main-navigation-row-text" {...styles.mainNavigationRowTypography}>
                                {quickOrderLink.title}
                            </Typography>
                        </PanelRow>
                    )}
                </StyledSection>
                {headerLinkListLinks.length > 0 && (
                    <StyledSection className="drawer-section-wrapper" {...styles.drawerSectionWrapper}>
                        {headerLinkListLinks.map((link, index) => (
                            <PanelRow
                                className="logo-links-row"
                                key={link.title}
                                {...(styles.logoLinks && omitSingle(styles.logoLinks, "typographyProps"))}
                                isCurrent={currentPageUrl === link.url}
                                onClick={closeDrawer}
                                href={link.url}
                                target={headerLinkListLinkFields[index].fields.openInNewWindow ? "_blank" : ""}
                            >
                                <Typography className="logo-link-title" {...styles.logoLinks?.typographyProps}>
                                    {link.title}
                                </Typography>
                            </PanelRow>
                        ))}
                    </StyledSection>
                )}
            </Drawer>
        </>
    );
};




function findVisibilityState(data: any): string | undefined {
    for (const key in data) {
        if (data[key].type === "Common/HeaderSignIn") {
            return data[key].generalFields.visibilityState;
        }
    }
    return undefined;
}

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(withTheme(NavigationDrawer)));
